// CRM Deliverability — folds the classic Mailboxes / Pools / Warmup / Personas /
// Signatures / Snippets / Suppressions screens into one CRM shell with tabs.
// Stage 1: shell + Mailboxes tab (the connection hub). Other tabs land next stages.
//
// Mailboxes tab = connection hub + fleet manager:
//   connect Google / M365 / Zoho via OAuth (/oauth/{provider}/connect anchor links),
//   or Zoho / generic SMTP via manual form; per-mailbox status, health, daily cap,
//   warmup flag, owner (shared 'webcite' vs client); reconnect errored OAuth; bulk ops.
// Mailboxes are a platform-level shared pool; owner_type='webcite' rows are shared
// across clients, client-owned rows belong to this tenant.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import CrmPools from './CrmPools';
import CrmPersonas from './CrmPersonas';
import CrmWarmup from './CrmWarmup';
import { SmtpConnectModal, SharedZohoModal, BulkEditModal } from './CrmMailboxConnect';
import CrmSnippets from './CrmSnippets';
import CrmSuppressions from './CrmSuppressions';

// Signatures live on the mailbox detail (per-mailbox) and the Personas tab (persona
// signature, which overrides the mailbox one at send time) — no standalone tab.
const TABS = ['Mailboxes', 'Pools', 'Warmup', 'Personas', 'Snippets', 'Suppressions', 'Postmaster'];
const IMPLEMENTED = new Set(['Mailboxes', 'Pools', 'Personas', 'Warmup', 'Snippets', 'Suppressions', 'Postmaster']);

const PROVIDERS = {
  google:    { label: 'Google',        oauth: true,  dot: '#EA4335' },
  microsoft: { label: 'Microsoft 365', oauth: true,  dot: '#2E8FFF' },
  zoho:      { label: 'Zoho',          oauth: true,  dot: '#E42527' },
  smtp:      { label: 'SMTP / IMAP',   oauth: false, dot: '#64748B' },
};
function providerMeta(p) { return PROVIDERS[p] || { label: p || 'Unknown', oauth: false, dot: '#64748B' }; }

const MB_STATUS = {
  active:       { label: 'Active',       cls: 'crmdl-st-green' },
  error:        { label: 'Error',        cls: 'crmdl-st-rose' },
  disconnected: { label: 'Disconnected', cls: 'crmdl-st-slate' },
  paused:       { label: 'Paused',       cls: 'crmdl-st-amber' },
};
function mbStatusMeta(s) { return MB_STATUS[s] || { label: s || 'Unknown', cls: 'crmdl-st-slate' }; }

function healthBand(score) {
  if (score == null) return { label: '—', cls: 'crmdl-h-none' };
  if (score >= 80) return { label: score + '', cls: 'crmdl-h-green' };
  if (score >= 50) return { label: score + '', cls: 'crmdl-h-amber' };
  return { label: score + '', cls: 'crmdl-h-rose' };
}

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CrmDeliverability() {
  const { clientId } = useOutletContext();
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return TABS.includes(t) ? t : 'Mailboxes';
  });

  return (
    <div className="crmdl-root">
      <div className="crmdl-tabs">
        {TABS.map(t => (
          <button
            key={t}
            className={'crmdl-tab' + (tab === t ? ' crmdl-tab--on' : '')}
            onClick={() => setTab(t)}
          >
            {t}{!IMPLEMENTED.has(t) && <span className="crmdl-soon">soon</span>}
          </button>
        ))}
      </div>
      <div className="crmdl-panel">
        {tab === 'Mailboxes' && <MailboxesTab clientId={clientId} />}
        {tab === 'Pools' && <CrmPools clientId={clientId} />}
        {tab === 'Personas' && <CrmPersonas clientId={clientId} />}
        {tab === 'Warmup' && <CrmWarmup clientId={clientId} />}
        {tab === 'Snippets' && <CrmSnippets clientId={clientId} />}
        {tab === 'Suppressions' && <CrmSuppressions clientId={clientId} />}
        {tab === 'Postmaster' && <PostmasterTab clientId={clientId} />}
        {!IMPLEMENTED.has(tab) && (
          <div className="crmdl-placeholder">
            <div className="crmdl-placeholder-inner">
              <div className="crmdl-placeholder-title">{tab}</div>
              <div className="crmdl-placeholder-sub">This tab is being rehoused next. For now it still lives in the classic Mail area.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Google Postmaster Tools: Gmail's own view of each sending domain, pulled daily.
const PM_STATUS = {
  ok:           { label: 'Reporting',            cls: 'crmdl-st-green' },
  no_data:      { label: 'No data yet',          cls: 'crmdl-st-slate', tip: 'Gmail only reports a domain once it sends to 50 or more Gmail users a day.' },
  not_verified: { label: 'Not verified',         cls: 'crmdl-st-amber', tip: 'Add and verify this domain at postmaster.google.com under the connected Google account.' },
  error:        { label: 'Error',                cls: 'crmdl-st-rose' },
};
const pct = (v, dp = 0) => (v == null ? '—' : (v * 100).toFixed(dp) + '%');
function spamCls(v) { if (v == null) return ''; if (v >= 0.003) return 'crmdl-st crmdl-st-rose'; if (v >= 0.001) return 'crmdl-st crmdl-st-amber'; return 'crmdl-st crmdl-st-green'; }

function PostmasterTab({ clientId }) {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const call = useCallback(async (method) => {
    const token = await getToken();
    const r = await fetch(`${API_BASE}/postmaster/${method === 'POST' ? 'sync' : 'status'}`, { method, headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`);
    return d;
  }, [getToken]);

  const load = useCallback(async () => {
    try { setError(null); setData(await call('GET')); } catch (e) { setError(e.message); }
  }, [call]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setBusy(true);
    try { setData(await call('POST')); toast.success('Postmaster data refreshed'); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const connectUrl = `${API_BASE}/oauth/google/postmaster/connect?client_id=${clientId}`;

  if (error) return <div className="crmdl-err">{error}</div>;
  if (!data) return <div className="crmdl-muted">Loading…</div>;

  return (
    <div className="crmdl-mb">
      <div className="crmdl-mb-head">
        <div className="crmdl-mb-stats">
          {data.connected
            ? <span className="crmdl-stat">Connected as <strong>{data.account.email}</strong></span>
            : <span className="crmdl-stat">Not connected</span>}
        </div>
        <div className="crmdl-mb-headbtns">
          <a className="crmdl-btn" href={connectUrl}>{data.connected ? 'Reconnect Google' : 'Connect Google'}</a>
          {data.connected && <button className="crmdl-btn crmdl-btn--primary" onClick={sync} disabled={busy}>{busy ? 'Refreshing…' : 'Refresh now'}</button>}
        </div>
      </div>

      {!data.connected ? (
        <div className="crmdl-empty">
          <div className="crmdl-empty-title">Connect Google Postmaster Tools</div>
          <div className="crmdl-empty-sub">Shows Gmail's own figures for each sending domain: spam complaint rate, SPF, DKIM and DMARC pass rates, and delivery errors. Verify your domains at postmaster.google.com first, then connect the same Google account here.</div>
          <a className="crmdl-btn crmdl-btn--primary" href={connectUrl}>Connect Google</a>
        </div>
      ) : (
        <div className="crmdl-mb-body">
          {(!data.domains || data.domains.length === 0) ? (
            <div className="crmdl-muted">No results yet. Press Refresh now.</div>
          ) : (
            <table className="crmca-tbl">
              <thead>
                <tr><th>Domain</th><th>Status</th><th>Spam rate</th><th>SPF</th><th>DKIM</th><th>DMARC</th><th>Delivery errors</th><th>Last refreshed</th></tr>
              </thead>
              <tbody>
                {data.domains.map(d => {
                  const st = PM_STATUS[d.status] || { label: d.status, cls: 'crmdl-st-slate' };
                  const L = d.latest || {};
                  const spam = L.spam_rate ? L.spam_rate.value : null;
                  return (
                    <tr key={d.domain}>
                      <td>{d.domain}</td>
                      <td><span className={'crmdl-st ' + st.cls} title={st.tip || d.last_error || ''}>{st.label}</span></td>
                      <td>{spam == null ? '—' : <span className={spamCls(spam)} title="Under 0.1% is healthy. Gmail starts filtering at 0.3%.">{pct(spam, 2)}</span>}</td>
                      <td>{pct(L.spf_rate && L.spf_rate.value)}</td>
                      <td>{pct(L.dkim_rate && L.dkim_rate.value)}</td>
                      <td>{pct(L.dmarc_rate && L.dmarc_rate.value)}</td>
                      <td>{L.delivery_errors ? L.delivery_errors.value : '—'}</td>
                      <td className="crmca-tbl-muted">{d.last_synced_at ? fmtDate(d.last_synced_at) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="crmdl-muted" style={{ marginTop: 10 }}>
            Refreshed daily. Gmail publishes each day's figures with a delay of a day or two, and only for domains sending to 50 or more Gmail users a day. Spam rate under 0.1% is healthy; at 0.3% Gmail starts filtering, and Sonar emails you.
          </div>
        </div>
      )}
    </div>
  );
}

function MailboxesTab({ clientId }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [connectOpen, setConnectOpen] = useState(false);
  const [smtpOpen, setSmtpOpen] = useState(false);
  const [capEditId, setCapEditId] = useState(null);
  const [capValue, setCapValue] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [poolModalOpen, setPoolModalOpen] = useState(false);
  const [sharedOpen, setSharedOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const oauthPollRef = useRef(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      // Platform-pool view: all mailboxes across clients, each with assigned_clients[].
      const res = await fetch(`${API_BASE}/admin/mailboxes`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load mailboxes (${res.status})`);
      const data = await res.json();
      setMailboxes(Array.isArray(data) ? data : (data.mailboxes || []));
    } catch (e) { if (!silent) setError(e.message); }
    finally { if (!silent) setLoading(false); }
  }, [clientId, getToken]);

  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const startOAuthPoll = useCallback(() => {
    if (oauthPollRef.current) clearInterval(oauthPollRef.current);
    let ticks = 0;
    oauthPollRef.current = setInterval(() => {
      ticks += 1;
      load({ silent: true });
      if (ticks >= 40) { clearInterval(oauthPollRef.current); oauthPollRef.current = null; }
    }, 3000);
  }, [load]);
  useEffect(() => () => { if (oauthPollRef.current) clearInterval(oauthPollRef.current); }, []);

  const connectOAuth = (provider, personal = false, dc = null) => {
    const url = `${API_BASE}/oauth/${provider}/connect?client_id=${clientId}${personal ? '&personal=1' : ''}${dc ? '&dc=' + dc : ''}`;
    window.open(url, '_blank', 'noopener');
    setConnectOpen(false);
    startOAuthPoll();
    toast('Complete the sign-in in the new tab, then come back. The list will refresh automatically.', { icon: '↗' });
  };

  // Landing back here from an OAuth tab: say what happened to a personal connect.
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('personal_refused')) toast.error(`${qs.get('connected') || 'That mailbox'} already sends campaigns, so it was left as a sending mailbox. Use a different address for 1-1 email.`, { duration: 8000 });
    else if (qs.get('personal') && qs.get('connected')) toast.success(`${qs.get('connected')} connected as your personal inbox`);
  }, []);

  const saveCap = async (m) => {
    const v = parseInt(capValue);
    if (isNaN(v) || v < 0) { toast.error('Enter a valid cap'); return; }
    setBusyId(m.id);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/${m.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ daily_cap: v }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      setCapEditId(null); setCapValue('');
      await load({ silent: true });
      toast.success('Daily cap updated');
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  const toggleWarmup = async (m) => {
    setBusyId(m.id);
    try {
      const token = await getToken();
      const action = m.warmup_enabled ? 'disable' : 'enable';
      const res = await fetch(`${API_BASE}/mailboxes/${m.id}/warmup/${action}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Warmup ${action} failed (${res.status})`);
      await load({ silent: true });
      toast.success(`Warmup ${action}d`);
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  // Seed inboxes receive inbox placement tests. Only Google and Microsoft can be read back.
  const toggleSeed = async (m) => {
    setBusyId(m.id);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/${m.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_seed: !m.is_seed }),
      });
      if (!res.ok) throw new Error(`Update failed (${res.status})`);
      await load({ silent: true });
      toast.success(m.is_seed ? `${m.email_address} is no longer a seed` : `${m.email_address} is now a placement test seed`);
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  const setStatus = async (m, status) => {
    setBusyId(m.id);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/${m.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(`Update failed (${res.status})`);
      await load({ silent: true });
      toast.success(`Mailbox ${status === 'active' ? 'activated' : 'paused'}`);
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  // First press disconnects (clears credentials, keeps the row so it can be reconnected).
  // On a mailbox that is already disconnected, the bin deletes it for good. Sent and
  // received email history stays on contact records either way.
  const removeMailbox = async (m) => {
    const hard = m.status === 'disconnected';
    const msg = hard
      ? `Delete ${m.email_address} permanently? It is removed from Sonar. Email history on contact records is kept.`
      : `Disconnect ${m.email_address}? It stops sending and its sign-in is cleared. Press the bin again afterwards to delete it for good.`;
    if (!window.confirm(msg)) return;
    setBusyId(m.id);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/${m.id}${hard ? '?hard=true' : ''}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setSelected(prev => { const n = new Set(prev); n.delete(m.id); return n; });
      await load({ silent: true });
      toast.success(hard ? 'Mailbox deleted' : 'Mailbox disconnected');
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  const bulkCap = async () => {
    const v = window.prompt('Set daily cap for ' + selected.size + ' mailbox(es) to:');
    if (v == null) return;
    const cap = parseInt(v);
    if (isNaN(cap) || cap < 0) { toast.error('Invalid cap'); return; }
    const ids = [...selected];
    const t = toast.loading(`Updating ${ids.length}…`);
    try {
      const token = await getToken();
      let ok = 0;
      for (const id of ids) {
        const r = await fetch(`${API_BASE}/mailboxes/${id}`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ daily_cap: cap }) });
        if (r.ok) ok++;
      }
      await load({ silent: true });
      toast.success(`Cap set on ${ok}/${ids.length}`, { id: t });
      setSelected(new Set());
    } catch (e) { toast.error(e.message, { id: t }); }
  };

  const bulkWarmup = async (enable) => {
    const ids = [...selected];
    const t = toast.loading(`${enable ? 'Enabling' : 'Disabling'} warmup on ${ids.length}…`);
    try {
      const token = await getToken();
      let ok = 0;
      for (const id of ids) {
        const r = await fetch(`${API_BASE}/mailboxes/${id}/warmup/${enable ? 'enable' : 'disable'}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) ok++;
      }
      await load({ silent: true });
      toast.success(`Warmup ${enable ? 'enabled' : 'disabled'} on ${ok}/${ids.length}`, { id: t });
      setSelected(new Set());
    } catch (e) { toast.error(e.message, { id: t }); }
  };

  const refreshTokens = async () => {
    setRefreshing(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/bulk-refresh`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: Array.from(selected) }) });
      if (!res.ok) throw new Error(`Refresh failed (${res.status})`);
      toast.success('Tokens refreshed');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setRefreshing(false); }
  };

  const bulkRemove = async () => {
    const ids = [...selected];
    const disconnected = new Set(mailboxes.filter(m => ids.includes(m.id) && m.status === 'disconnected').map(m => m.id));
    const live = ids.length - disconnected.size;
    const parts = [];
    if (live) parts.push(`disconnect ${live}`);
    if (disconnected.size) parts.push(`permanently delete ${disconnected.size} already disconnected`);
    if (!window.confirm(`This will ${parts.join(' and ')}. Email history on contact records is kept. Continue?`)) return;
    const t = toast.loading(`Removing ${ids.length}…`);
    try {
      const token = await getToken();
      let ok = 0;
      for (const id of ids) {
        const r = await fetch(`${API_BASE}/mailboxes/${id}${disconnected.has(id) ? '?hard=true' : ''}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) ok++;
      }
      await load({ silent: true });
      toast.success(`Removed ${ok}/${ids.length}`, { id: t });
      setSelected(new Set());
    } catch (e) { toast.error(e.message, { id: t }); }
  };

  const filtered = useMemo(() => {
    return mailboxes.filter(m => {
      if (providerFilter !== 'all' && m.provider !== providerFilter) return false;
      if (statusFilter !== 'all' && m.status !== statusFilter) return false;
      if (ownerFilter === 'shared' && m.owner_type !== 'webcite') return false;
      if (ownerFilter === 'client' && (m.owner_type === 'webcite' || m.owner_type === 'personal')) return false;
      if (ownerFilter === 'personal' && m.owner_type !== 'personal') return false;
      if (query) {
        const q = query.toLowerCase();
        if (!(m.email_address || '').toLowerCase().includes(q) && !(m.display_name || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [mailboxes, providerFilter, statusFilter, ownerFilter, query]);

  const providers = useMemo(() => Array.from(new Set(mailboxes.map(m => m.provider).filter(Boolean))), [mailboxes]);
  const counts = useMemo(() => {
    const c = { total: mailboxes.length, active: 0, error: 0, warming: 0 };
    mailboxes.forEach(m => { if (m.status === 'active') c.active++; if (m.status === 'error') c.error++; if (m.warmup_enabled) c.warming++; });
    return c;
  }, [mailboxes]);

  const allVisibleSelected = filtered.length > 0 && filtered.every(m => selected.has(m.id));
  const toggleAll = () => {
    if (allVisibleSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map(m => m.id)));
  };
  const toggleOne = (id) => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="crmdl-mb">
      <div className="crmdl-mb-head">
        <div className="crmdl-mb-stats">
          <span className="crmdl-stat"><strong>{counts.total}</strong> mailboxes</span>
          <span className="crmdl-stat crmdl-stat--green"><strong>{counts.active}</strong> active</span>
          {counts.error > 0 && <span className="crmdl-stat crmdl-stat--rose"><strong>{counts.error}</strong> error</span>}
          <span className="crmdl-stat"><strong>{counts.warming}</strong> warming up</span>
        </div>
        <div className="crmdl-mb-headbtns">
          <button className="crmdl-btn" onClick={() => setSmtpOpen(true)}>+ SMTP / IMAP</button>
          <button className="crmdl-btn" onClick={() => setSharedOpen(true)}>+ Zoho Shared</button>
          <button className="crmdl-btn" onClick={() => connectOAuth('google', true)} title="Your own Gmail or Google Workspace inbox, for 1-1 email from contact records. Never used for campaigns or warmup.">+ Personal inbox</button>
          <button className="crmdl-btn crmdl-btn--primary" onClick={() => setConnectOpen(true)}>+ Connect mailbox</button>
        </div>
      </div>

      <div className="crmdl-mb-filters">
        <input className="crmdl-input crmdl-input--search" type="text" placeholder="Search email or name…" value={query} onChange={e => setQuery(e.target.value)} />
        {providers.length > 1 && (
          <select className="crmdl-select" value={providerFilter} onChange={e => setProviderFilter(e.target.value)}>
            <option value="all">All providers</option>
            {providers.map(p => <option key={p} value={p}>{providerMeta(p).label}</option>)}
          </select>
        )}
        <select className="crmdl-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All status</option>
          <option value="active">Active</option>
          <option value="error">Error</option>
          <option value="paused">Paused</option>
          <option value="disconnected">Disconnected</option>
        </select>
        <select className="crmdl-select" value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}>
          <option value="all">All owners</option>
          <option value="shared">Shared (Webcite)</option>
          <option value="client">Client-owned</option>
          <option value="personal">Personal inboxes</option>
        </select>
      </div>

      {selected.size > 0 && (
        <div className="crmdl-bulkbar">
          <span className="crmdl-bulkbar-n">{selected.size} selected</span>
          <span className="crmdl-dot">·</span>
          <button className="crmdl-btn crmdl-btn--sm" onClick={bulkCap}>Set daily cap</button>
          <button className="crmdl-btn crmdl-btn--sm" onClick={() => bulkWarmup(true)}>Warmup on</button>
          <button className="crmdl-btn crmdl-btn--sm" onClick={() => bulkWarmup(false)}>Warmup off</button>
          <button className="crmdl-btn crmdl-btn--sm" onClick={() => setPoolModalOpen(true)}>Add to pool</button>
          <button className="crmdl-btn crmdl-btn--sm" onClick={() => setBulkEditOpen(true)}>Bulk edit</button>
          <button className="crmdl-btn crmdl-btn--sm" onClick={refreshTokens} disabled={refreshing}>{refreshing ? 'Refreshing…' : 'Refresh tokens'}</button>
          <button className="crmdl-btn crmdl-btn--sm crmdl-btn--danger" onClick={bulkRemove}>Remove</button>
          <span className="crmdl-spacer" />
          <button className="crmdl-btn crmdl-btn--sm crmdl-btn--ghost" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      <div className="crmdl-mb-body">
        {loading ? <div className="crmdl-muted">Loading mailboxes…</div>
          : error ? <div className="crmdl-err">{error}</div>
          : mailboxes.length === 0 ? (
            <div className="crmdl-empty">
              <div className="crmdl-empty-title">No mailboxes connected</div>
              <div className="crmdl-empty-sub">Connect a Google, Microsoft 365, or Zoho mailbox, or add one via SMTP/IMAP, to start sending.</div>
              <button className="crmdl-btn crmdl-btn--primary" onClick={() => setConnectOpen(true)}>+ Connect mailbox</button>
            </div>
          ) : filtered.length === 0 ? <div className="crmdl-muted">No mailboxes match the current filter.</div>
          : (
            <div className="crmdl-rows">
              <div className="crmdl-rows-head">
                <label className="crmdl-rows-all"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} /> Select all</label>
                <span className="crmdl-rows-count">{filtered.length} of {mailboxes.length}</span>
              </div>
              {filtered.map(m => {
                const pm = providerMeta(m.provider);
                const sm = mbStatusMeta(m.status);
                const busy = busyId === m.id;
                const shared = m.owner_type === 'webcite';
                const personalBox = m.owner_type === 'personal';
                const cap = m.daily_cap || 0;
                const sent = m.sent_today || 0;
                const pacePct = cap > 0 ? Math.min(100, Math.round((sent / cap) * 100)) : 0;
                const sel = selected.has(m.id);
                const hc = m.status === 'active' ? 'var(--crm-green)' : m.status === 'error' ? 'var(--crm-rose)' : m.status === 'paused' ? 'var(--crm-amber)' : 'var(--crm-slate)';
                return (
                  <div key={m.id} className={'crmdl-row' + (sel ? ' crmdl-row--sel' : '')}>
                    <label className="crmdl-row-chk" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={sel} onChange={() => toggleOne(m.id)} />
                    </label>
                    <span className="crmdl-row-dot" style={{ background: hc }} title={sm.label} />
                    <div className="crmdl-row-id">
                      <button className="crmdl-row-email" onClick={() => navigate(`/clients/${clientId}/crm/deliverability/mailboxes/${m.id}`)}>{m.email_address}</button>
                      <div className="crmdl-row-sub">
                        <span className="crmdl-row-prov"><span className="crmdl-prov-dot" style={{ background: pm.dot }} />{pm.label}</span>
                        {m.display_name && <><span className="crmdl-row-sep">·</span><span>{m.display_name}</span></>}
                        {shared && <><span className="crmdl-row-sep">·</span><span className="crmdl-row-shared">Shared</span></>}
                        {personalBox && <><span className="crmdl-row-sep">·</span><span className="crmdl-row-personal">Personal · 1-1 email only</span></>}
                        {m.paused_reason && <><span className="crmdl-row-sep">·</span><span className="crmdl-row-personal" title={m.paused_reason}>{m.paused_reason}</span></>}
                      </div>
                    </div>
                    <div className="crmdl-row-clients">
                      {(m.assigned_clients && m.assigned_clients.length) ? (
                        m.assigned_clients.map(c => <span key={c.id} className="crmdl-row-cchip" title={c.sector || ''}>{c.name}</span>)
                      ) : <span className="crmdl-row-unassigned">Unassigned</span>}
                    </div>
                    <div className="crmdl-row-pace">
                      <div className="crmdl-row-pacebar"><div className="crmdl-row-pacefill" style={{ width: pacePct + '%' }} /></div>
                      <div className="crmdl-row-pacelbl">{sent} / {cap}{m.daily_cap_ceiling ? <span className="crmdl-row-ceil"> → {m.daily_cap_ceiling}</span> : ''}</div>
                    </div>
                    {personalBox ? (
                      <span className="crmdl-row-warm crmdl-row-warm--na" title="Personal inboxes never warm up or send campaigns">Syncs contacts only</span>
                    ) : (
                      <button className={'crmdl-row-warm' + (m.warmup_enabled ? ' crmdl-row-warm--on' : '')} disabled={busy} onClick={() => toggleWarmup(m)} title="Toggle warmup">
                        {m.warmup_enabled ? '● Warming' : '○ Warmup off'}
                      </button>
                    )}
                    <span className={'crmdl-st ' + sm.cls}>{sm.label}</span>
                    <div className="crmdl-row-actions">
                      {(m.provider === 'google' || m.provider === 'microsoft') && (
                        <button className={'crmdl-btn crmdl-btn--sm' + (m.is_seed ? ' crmdl-btn--primary' : '')} disabled={busy} onClick={() => toggleSeed(m)}
                          title="Seed inboxes receive inbox placement tests. Use Google Workspace or Microsoft 365 mailboxes on a different domain from your senders.">
                          {m.is_seed ? 'Seed ✓' : 'Seed'}
                        </button>
                      )}
                      {m.status === 'error' && <a className="crmdl-row-reconnect" href={`${API_BASE}/oauth/${m.provider}/connect?client_id=${clientId}&mailbox_id=${m.id}${personalBox ? '&personal=1' : ''}`} target="_blank" rel="noopener noreferrer" onClick={() => startOAuthPoll()}>Reconnect</a>}
                      {m.status === 'active' && <button className="crmdl-iconbtn" disabled={busy} title="Pause" onClick={() => setStatus(m, 'paused')}>❙❙</button>}
                      {m.status === 'paused' && <button className="crmdl-iconbtn" disabled={busy} title="Activate" onClick={() => setStatus(m, 'active')}>▶</button>}
                      <button className="crmdl-iconbtn crmdl-iconbtn--danger" disabled={busy} title={m.status === 'disconnected' ? 'Delete permanently' : 'Disconnect'} onClick={() => removeMailbox(m)}>🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {connectOpen && <ConnectModal onClose={() => setConnectOpen(false)} onOAuth={connectOAuth} onSmtp={() => { setConnectOpen(false); setSmtpOpen(true); }} />}
      {smtpOpen && <SmtpConnectModal onClose={() => setSmtpOpen(false)} onImported={async () => { setSmtpOpen(false); await load(); }} />}
      {sharedOpen && <SharedZohoModal onClose={() => setSharedOpen(false)} onImported={async () => { setSharedOpen(false); await load(); }} />}
      {bulkEditOpen && <BulkEditModal mailboxIds={Array.from(selected)} onClose={() => setBulkEditOpen(false)} onDone={async () => { setBulkEditOpen(false); setSelected(new Set()); await load(); }} />}
      {poolModalOpen && <AddToPoolModal clientId={clientId} mailboxIds={Array.from(selected)} onClose={() => setPoolModalOpen(false)} onDone={async () => { setPoolModalOpen(false); setSelected(new Set()); await load(); }} />}
    </div>
  );
}

function ConnectModal({ onClose, onOAuth, onSmtp }) {
  return (
    <div className="crmdl-ovl" onClick={onClose}>
      <div className="crmdl-dialog" onClick={e => e.stopPropagation()}>
        <div className="crmdl-dialog-head"><h3>Connect a mailbox</h3></div>
        <div className="crmdl-dialog-sub">Choose how you want to connect. OAuth is recommended for Google, Microsoft 365, and Zoho.</div>
        <div className="crmdl-provgrid">
          <button className="crmdl-provbtn" onClick={() => onOAuth('google')}>
            <span className="crmdl-prov-dot" style={{ background: PROVIDERS.google.dot }} />
            <span className="crmdl-provbtn-l"><span className="crmdl-provbtn-name">Google</span><span className="crmdl-provbtn-sub">Gmail / Workspace · OAuth</span></span>
          </button>
          <button className="crmdl-provbtn" onClick={() => onOAuth('microsoft')}>
            <span className="crmdl-prov-dot" style={{ background: PROVIDERS.microsoft.dot }} />
            <span className="crmdl-provbtn-l"><span className="crmdl-provbtn-name">Microsoft 365</span><span className="crmdl-provbtn-sub">Outlook / Exchange · OAuth</span></span>
          </button>
          <button className="crmdl-provbtn" onClick={() => onOAuth('zoho')}>
            <span className="crmdl-prov-dot" style={{ background: PROVIDERS.zoho.dot }} />
            <span className="crmdl-provbtn-l"><span className="crmdl-provbtn-name">Zoho</span><span className="crmdl-provbtn-sub">Zoho Mail (EU and others) · OAuth</span></span>
          </button>
          <button className="crmdl-provbtn" onClick={() => onOAuth('zoho', false, 'uk')}>
            <span className="crmdl-prov-dot" style={{ background: PROVIDERS.zoho.dot }} />
            <span className="crmdl-provbtn-l"><span className="crmdl-provbtn-name">Zoho UK</span><span className="crmdl-provbtn-sub">Accounts created on zoho.uk · OAuth</span></span>
          </button>
          <button className="crmdl-provbtn" onClick={onSmtp}>
            <span className="crmdl-prov-dot" style={{ background: PROVIDERS.smtp.dot }} />
            <span className="crmdl-provbtn-l"><span className="crmdl-provbtn-name">SMTP / IMAP</span><span className="crmdl-provbtn-sub">Any provider · manual credentials</span></span>
          </button>
        </div>
        <div className="crmdl-dialog-foot">
          <button className="crmdl-btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function SmtpModal({ clientId, onClose, onAdded }) {
  const { getToken } = useAuth();
  const [form, setForm] = useState({
    email_address: '', display_name: '',
    smtp_host: '', smtp_port: '587', smtp_username: '', smtp_password: '',
    imap_host: '', imap_port: '993', imap_username: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.email_address || !form.smtp_host || !form.smtp_username || !form.smtp_password) {
      setError('Email, SMTP host, username and password are required.'); return;
    }
    setSaving(true); setError(null);
    try {
      const token = await getToken();
      const body = {
        provider: 'smtp', auth_type: 'smtp',
        email_address: form.email_address.trim(),
        display_name: form.display_name.trim() || null,
        smtp_host: form.smtp_host.trim(), smtp_port: parseInt(form.smtp_port) || 587,
        smtp_username: form.smtp_username.trim(), smtp_password: form.smtp_password,
        imap_host: form.imap_host.trim() || form.smtp_host.trim(),
        imap_port: parseInt(form.imap_port) || 993,
        imap_username: form.imap_username.trim() || form.smtp_username.trim(),
      };
      const res = await fetch(`${API_BASE}/clients/${clientId}/mailboxes`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed to add mailbox (${res.status})`);
      toast.success('Mailbox added');
      onAdded();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="crmdl-ovl" onClick={onClose}>
      <div className="crmdl-dialog crmdl-dialog--wide" onClick={e => e.stopPropagation()}>
        <div className="crmdl-dialog-head"><h3>Add mailbox via SMTP / IMAP</h3></div>
        <div className="crmdl-dialog-sub">Enter the sending (SMTP) and receiving (IMAP) credentials. IMAP defaults to the SMTP values if left blank.</div>
        <div className="crmdl-dialog-scroll">
          <div className="crmdl-form-row">
            <label className="crmdl-form-lbl">Email address</label>
            <input className="crmdl-input" value={form.email_address} onChange={e => set('email_address', e.target.value)} placeholder="sender@yourdomain.com" autoFocus />
          </div>
          <div className="crmdl-form-row">
            <label className="crmdl-form-lbl">Display name (optional)</label>
            <input className="crmdl-input" value={form.display_name} onChange={e => set('display_name', e.target.value)} placeholder="Jane at Acme" />
          </div>
          <div className="crmdl-form-sec">SMTP (sending)</div>
          <div className="crmdl-form-grid">
            <div className="crmdl-form-row"><label className="crmdl-form-lbl">Host</label><input className="crmdl-input" value={form.smtp_host} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.yourprovider.com" /></div>
            <div className="crmdl-form-row"><label className="crmdl-form-lbl">Port</label><input className="crmdl-input" type="number" value={form.smtp_port} onChange={e => set('smtp_port', e.target.value)} /></div>
            <div className="crmdl-form-row"><label className="crmdl-form-lbl">Username</label><input className="crmdl-input" value={form.smtp_username} onChange={e => set('smtp_username', e.target.value)} placeholder="usually the full email" /></div>
            <div className="crmdl-form-row"><label className="crmdl-form-lbl">Password</label><input className="crmdl-input" type="password" value={form.smtp_password} onChange={e => set('smtp_password', e.target.value)} /></div>
          </div>
          <div className="crmdl-form-sec">IMAP (receiving) <span className="crmdl-form-secnote">optional — defaults to SMTP</span></div>
          <div className="crmdl-form-grid">
            <div className="crmdl-form-row"><label className="crmdl-form-lbl">Host</label><input className="crmdl-input" value={form.imap_host} onChange={e => set('imap_host', e.target.value)} placeholder="imap.yourprovider.com" /></div>
            <div className="crmdl-form-row"><label className="crmdl-form-lbl">Port</label><input className="crmdl-input" type="number" value={form.imap_port} onChange={e => set('imap_port', e.target.value)} /></div>
            <div className="crmdl-form-row"><label className="crmdl-form-lbl">Username</label><input className="crmdl-input" value={form.imap_username} onChange={e => set('imap_username', e.target.value)} placeholder="defaults to SMTP username" /></div>
          </div>
          {error && <div className="crmdl-err">{error}</div>}
        </div>
        <div className="crmdl-dialog-foot">
          <button className="crmdl-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="crmdl-btn crmdl-btn--primary" onClick={submit} disabled={saving}>{saving ? 'Adding…' : 'Add mailbox'}</button>
        </div>
      </div>
    </div>
  );
}

function AddToPoolModal({ clientId, mailboxIds, onClose, onDone }) {
  const { getToken } = useAuth();
  const [pools, setPools] = useState([]);
  const [pickedPoolId, setPickedPoolId] = useState('');
  const [creatingNew, setCreatingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const r = await fetch(`${API_BASE}/clients/${clientId}/pools`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(`Could not load pools (${r.status})`);
        const data = await r.json();
        setPools(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length === 0) setCreatingNew(true);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [clientId, getToken]);

  const canSubmit = creatingNew ? newName.trim().length > 0 : !!pickedPoolId;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const body = creatingNew
        ? { new_pool: { name: newName.trim(), description: newDesc.trim() || null }, mailbox_ids: mailboxIds }
        : { pool_id: parseInt(pickedPoolId), mailbox_ids: mailboxIds };
      const r = await fetch(`${API_BASE}/clients/${clientId}/pools/bulk-add-mailboxes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);
      toast.success(`Added ${mailboxIds.length} mailbox${mailboxIds.length === 1 ? '' : 'es'} to pool`);
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <div className="crmdl-ovl" onClick={onClose}>
      <div className="crmdl-dialog" onClick={e => e.stopPropagation()}>
        <div className="crmdl-dialog-head"><h3>Add to pool</h3></div>
        <div className="crmdl-dialog-sub">{mailboxIds.length} mailbox{mailboxIds.length === 1 ? '' : 'es'} selected</div>
        <div className="crmdl-dialog-scroll">
          {loading ? (
            <div className="crmdl-dialog-sub">Loading pools…</div>
          ) : (
            <>
              {!creatingNew && (
                <div className="crmdl-form-row">
                  <label className="crmdl-form-lbl">Pool</label>
                  <select className="crmdl-input" value={pickedPoolId} onChange={e => setPickedPoolId(e.target.value)}>
                    <option value="">{pools.length === 0 ? 'No pools yet — create one below' : 'Select a pool…'}</option>
                    {pools.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              {!creatingNew ? (
                <button type="button" className="crmdl-link-btn" onClick={() => { setCreatingNew(true); setPickedPoolId(''); }}>+ Create new pool</button>
              ) : (
                <div className="crmdl-newpool">
                  <div className="crmdl-form-row"><label className="crmdl-form-lbl">New pool name</label><input className="crmdl-input" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. UK accountants outbound" autoFocus /></div>
                  <div className="crmdl-form-row"><label className="crmdl-form-lbl">Description (optional)</label><input className="crmdl-input" value={newDesc} onChange={e => setNewDesc(e.target.value)} /></div>
                  {pools.length > 0 && <button type="button" className="crmdl-link-btn" onClick={() => { setCreatingNew(false); setNewName(''); setNewDesc(''); }}>Use an existing pool instead</button>}
                </div>
              )}
            </>
          )}
          {error && <div className="crmdl-err">{error}</div>}
        </div>
        <div className="crmdl-dialog-foot">
          <button className="crmdl-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="crmdl-btn crmdl-btn--primary" onClick={submit} disabled={!canSubmit || saving}>{saving ? 'Adding…' : 'Add to pool'}</button>
        </div>
      </div>
    </div>
  );
}
